import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { activeGrant, useStaffGate } from '../lib/staffContext';
import { StaffShell } from '../ui/StaffShell';
import { ContextError } from '../ui/ContextError';
import { roleDescription, roleLabel } from '../ui/roles';
import { useI18n } from '../i18n';
import { useDocumentTitle } from '../ui/a11y';
import { ReadinessBanner } from './teacher/ReadinessBanner';
import { useSchoolDates } from './head/useSchoolDates';

// Home for roles whose own screens arrive in later build steps (school admin:
// steps 6–11; counsellor: the insight workflows). Says plainly what this role
// will be able to do and that it isn't built yet, rather than showing another
// role's screen.
export function RoleHome() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { ctx, error } = useStaffGate(['school_admin', 'counsellor']);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  if (error) return <ContextError message={error} />;
  if (!ctx || !ctx.active_role) return <main className="page-loading" role="status">{t.loading}</main>;

  const grant = activeGrant(ctx);
  const label = roleLabel(t, ctx.active_role);

  return (
    <StaffShell
      role={label}
      nav={[
        { icon: '🏠', label: t['home.nav'], active: true },
        ...(ctx.active_role === 'school_admin'
          ? [
              { icon: '📁', label: t['h2.navCompliance'], href: '/compliance' },
              { icon: '📅', label: t['r3.nav.calendar'], href: '/calendar' },
              { icon: '📋', label: t['r5.sq.nav'], href: '/questions' },
            ]
          : []),
        { icon: '🫶', label: t['r3.me.nav'], href: '/me/checkin' },
      ]}
      title={t['home.title'](label)}
      who={ctx.display_name}
      ctx={ctx}
      onSignOut={signOut}
    >
      <section className="panel p5 role-home" aria-labelledby="home-what">
        <h2 className="panel-title" id="home-what">
          {t['home.whatTitle']}
        </h2>
        <p>{roleDescription(t, ctx.active_role)}</p>
        {grant?.school_name && <p className="text-muted">{t['home.school'](grant.school_name)}</p>}
        <p className="notice">{t['home.comingSoon']}</p>
      </section>
      {ctx.active_role === 'school_admin' && <SchoolAdminSetup />}
    </StaffShell>
  );
}

// K1, B16: the school admin sees what blocks check-ins and which classes
// have no teacher, until their own setup screens exist.
function SchoolAdminSetup() {
  const { t } = useI18n();
  const facts = useSchoolDates();
  if (!facts) return null;
  return (
    <>
      <ReadinessBanner state={facts.state} />
      {facts.classes_without_teacher && facts.classes_without_teacher.length > 0 && (
        <p className="notice notice-block">{t['r4.noTeacher'](facts.classes_without_teacher.join(', '))}</p>
      )}
    </>
  );
}

// Signed in, but holding no usable role (e.g. every role revoked, or the
// school is suspended).
export function NoRoles() {
  const navigate = useNavigate();
  const { t } = useI18n();
  useDocumentTitle(t['noRoles.title']);
  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }
  return (
    <main className="not-found">
      <div>
        <h1>{t['noRoles.title']}</h1>
        <p className="text-muted">{t['noRoles.body']}</p>
        <button type="button" className="btn btn-primary" onClick={signOut}>
          {t.signOut}
        </button>
      </div>
    </main>
  );
}
